/**
 * Finance — Phase 1: Chart of Accounts, Journal Entries, Expenses (GST/TDS),
 * Budgets + Scenarios, Multi-currency, GSTIN registry, GSTR export.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import AdvancedTable from "@/components/table/AdvancedTable";
import BudgetPlanningDialog from "@/components/finance/BudgetPlanningDialog";
import PlanningDashboard from "@/components/finance/PlanningDashboard";
import {
  Wallet, BookOpen, Receipt, PieChart, Settings2, Download, Plus, TrendingUp, TrendingDown, Landmark, Coins, LineChart,
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/lib/apiError";

const ACCT_TYPES = ["asset", "liability", "equity", "revenue", "expense"];
const SUBTYPES = {
  asset: ["current_asset", "non_current_asset", "bank", "cash", "receivable", "inventory", "fixed_asset"],
  liability: ["current_liability", "non_current_liability", "payable", "gst_payable", "tds_payable"],
  equity: ["equity"],
  revenue: ["operating_revenue", "non_operating_revenue"],
  expense: ["direct_expense", "indirect_expense", "depreciation", "tax_expense"],
};

const fmtMoney = (n, ccy = "INR") => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(v);
  } catch {
    return `${ccy} ${v.toFixed(2)}`;
  }
};

export default function Finance() {
  const { activeWorkspaceId } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "dashboard");
  useEffect(() => { setSp((p) => { const n = new URLSearchParams(p); n.set("tab", tab); return n; }, { replace: true }); }, [tab, setSp]);

  const wsId = activeWorkspaceId;

  return (
    <div className="space-y-6" data-testid="finance-page">
      <PageHeader title="Finance" subtitle="Accounting, expenses, budgets, GST/TDS compliance — a single ledger for the whole workspace." icon={Wallet} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="dashboard" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="finance-tab-dashboard"><PieChart className="w-4 h-4 mr-2" />Dashboard</TabsTrigger>
          <TabsTrigger value="accounts" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="finance-tab-accounts"><Landmark className="w-4 h-4 mr-2" />Accounts</TabsTrigger>
          <TabsTrigger value="journal" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="finance-tab-journal"><BookOpen className="w-4 h-4 mr-2" />Journal</TabsTrigger>
          <TabsTrigger value="expenses" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="finance-tab-expenses"><Receipt className="w-4 h-4 mr-2" />Expenses</TabsTrigger>
          <TabsTrigger value="budgets" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="finance-tab-budgets"><TrendingUp className="w-4 h-4 mr-2" />Budgets</TabsTrigger>
          <TabsTrigger value="planning" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="finance-tab-planning"><LineChart className="w-4 h-4 mr-2" />Planning</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="finance-tab-settings"><Settings2 className="w-4 h-4 mr-2" />Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6"><FinanceDashboard wsId={wsId} /></TabsContent>
        <TabsContent value="accounts" className="mt-6"><AccountsTab wsId={wsId} /></TabsContent>
        <TabsContent value="journal" className="mt-6"><JournalTab wsId={wsId} /></TabsContent>
        <TabsContent value="expenses" className="mt-6"><ExpensesTab wsId={wsId} /></TabsContent>
        <TabsContent value="budgets" className="mt-6"><BudgetsTab wsId={wsId} /></TabsContent>
        <TabsContent value="planning" className="mt-6"><PlanningDashboard wsId={wsId} /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab wsId={wsId} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ───────────────────── Dashboard ─────────────────────

function FinanceDashboard({ wsId }) {
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/settings`).then((r) => setSettings(r.data));
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccounts(r.data.accounts || []));
    api.get(`/finance/workspaces/${wsId}/journal-entries?limit=10`).then((r) => setEntries(r.data.entries || []));
    api.get(`/finance/workspaces/${wsId}/expenses?limit=20`).then((r) => setExpenses(r.data.expenses || []));
  }, [wsId]);

  const ccy = settings?.base_currency || "INR";
  const kpis = useMemo(() => {
    // Assets & expenses have debit-normal side (balance_base is +ve on debit).
    // Liabilities, equity & revenue have credit-normal side — the raw signed
    // balance_base goes negative on credit, so we flip sign for display.
    const debitSide = (t) => accounts.filter((a) => a.type === t).reduce((s, a) => s + (a.balance_base || 0), 0);
    const creditSide = (t) => accounts.filter((a) => a.type === t).reduce((s, a) => s - (a.balance_base || 0), 0);
    return {
      assets: debitSide("asset"),
      liab: creditSide("liability"),
      revenue: creditSide("revenue"),
      expenses: debitSide("expense"),
    };
  }, [accounts]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi label="Total assets" value={fmtMoney(kpis.assets, ccy)} icon={Landmark} tone="emerald" testid="kpi-assets" />
        <Kpi label="Total liabilities" value={fmtMoney(kpis.liab, ccy)} icon={Coins} tone="amber" testid="kpi-liab" />
        <Kpi label="Revenue (YTD)" value={fmtMoney(kpis.revenue, ccy)} icon={TrendingUp} tone="blue" testid="kpi-rev" />
        <Kpi label="Expenses (YTD)" value={fmtMoney(kpis.expenses, ccy)} icon={TrendingDown} tone="rose" testid="kpi-exp" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Recent journal entries</h3>
              <Badge variant="outline" data-testid="dashboard-entry-count">{entries.length}</Badge>
            </div>
            <div className="space-y-2 max-h-[320px] overflow-auto">
              {entries.length === 0 && <p className="text-sm text-muted-foreground">No entries yet.</p>}
              {entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between border-b py-2 text-sm">
                  <div>
                    <div className="font-medium">{e.memo || e.reference || "Manual entry"}</div>
                    <div className="text-xs text-muted-foreground">{e.entry_date} · {e.basis} · {e.currency}</div>
                  </div>
                  <div className="font-mono">{fmtMoney(e.total_debit_native, e.currency)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Recent expenses</h3>
              <Badge variant="outline" data-testid="dashboard-expense-count">{expenses.length}</Badge>
            </div>
            <div className="space-y-2 max-h-[320px] overflow-auto">
              {expenses.length === 0 && <p className="text-sm text-muted-foreground">No expenses yet.</p>}
              {expenses.map((x) => (
                <div key={x.id} className="flex items-center justify-between border-b py-2 text-sm">
                  <div>
                    <div className="font-medium">{x.vendor_name}</div>
                    <div className="text-xs text-muted-foreground">{x.expense_date} · GST {(x.cgst_pct + x.sgst_pct + x.igst_pct).toFixed(0)}% · TDS {x.tds_rate}%</div>
                  </div>
                  <div className="font-mono">{fmtMoney(x.gross_amount, x.currency)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "blue", testid }) {
  const tones = {
    emerald: "text-emerald-600 border-emerald-500/30",
    amber: "text-amber-600 border-amber-500/30",
    blue: "text-blue-600 border-blue-500/30",
    rose: "text-rose-600 border-rose-500/30",
  };
  return (
    <Card className={`border ${tones[tone]}`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-semibold mt-1 font-mono" data-testid={`${testid}-value`}>{value}</div>
          </div>
          <Icon className={`w-8 h-8 ${tones[tone]}`} />
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────── Accounts ─────────────────────

function AccountsTab({ wsId }) {
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "expense", subtype: "indirect_expense" });

  const load = () => api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccounts(r.data.accounts || []));
  useEffect(() => { if (wsId) load(); }, [wsId]);

  const create = async () => {
    try {
      await api.post(`/finance/workspaces/${wsId}/accounts`, form);
      toast.success("Account created");
      setOpen(false); setForm({ code: "", name: "", type: "expense", subtype: "indirect_expense" });
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to create"));
    }
  };

  const filtered = accounts.filter((a) => !q || `${a.code} ${a.name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input placeholder="Search code or name…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="accounts-search" />
        <div className="flex-1" />
        <Button onClick={() => setOpen(true)} data-testid="accounts-add-btn"><Plus className="w-4 h-4 mr-2" />New account</Button>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="p-3">Code</th><th className="p-3">Name</th><th className="p-3">Type</th><th className="p-3">Subtype</th>
              <th className="p-3 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className="border-t" data-testid={`account-row-${a.code}`}>
                <td className="p-3 font-mono">{a.code}</td>
                <td className="p-3">{a.name}</td>
                <td className="p-3"><Badge variant="outline">{a.type}</Badge></td>
                <td className="p-3 text-muted-foreground">{a.subtype}</td>
                <td className="p-3 text-right font-mono">{fmtMoney(
                  ["liability", "equity", "revenue"].includes(a.type) ? -a.balance_base : a.balance_base,
                  "INR",
                )}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No accounts.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="accounts-create-dialog">
          <DialogHeader>
            <DialogTitle>New account</DialogTitle>
            <DialogDescription>Add a chart-of-accounts entry. Codes must be unique.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="account-code-input" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" /></div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, subtype: SUBTYPES[v][0] })}>
                <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subtype</Label>
              <Select value={form.subtype} onValueChange={(v) => setForm({ ...form, subtype: v })}>
                <SelectTrigger data-testid="account-subtype-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SUBTYPES[form.type].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={create} data-testid="account-create-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────── Journal ─────────────────────

function JournalTab({ wsId }) {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entry_date: new Date().toISOString().slice(0, 10), memo: "", currency: "INR", basis: "accrual", lines: [{ account_id: "", debit: 0, credit: 0 }, { account_id: "", debit: 0, credit: 0 }] });

  const load = () => api.get(`/finance/workspaces/${wsId}/journal-entries?limit=100`).then((r) => setEntries(r.data.entries || []));
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccounts(r.data.accounts || []));
  }, [wsId]);

  const totals = useMemo(() => {
    const d = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const c = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { d, c, balanced: Math.abs(d - c) < 0.005 && d > 0 };
  }, [form.lines]);

  const updateLine = (i, patch) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], ...patch };
    setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { account_id: "", debit: 0, credit: 0 }] });
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const submit = async () => {
    try {
      const cleanLines = form.lines
        .filter((l) => l.account_id)
        .map((l) => ({ ...l, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 }));
      await api.post(`/finance/workspaces/${wsId}/journal-entries`, { ...form, lines: cleanLines });
      toast.success("Journal entry posted");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{entries.length} entries</div>
        <Button onClick={() => setOpen(true)} data-testid="journal-add-btn"><Plus className="w-4 h-4 mr-2" />New entry</Button>
      </div>
      <Card><CardContent className="p-0">
        <AdvancedTable
          tableKey="journal-entries"
          title="Journal entries"
          rows={entries}
          columns={[
            { key: "entry_date", label: "Date", width: 120, sortable: true, filterType: "date-range", frozen: true },
            { key: "memo", label: "Memo", width: 340, sortable: true, filterType: "text",
              render: (e) => (
                <span>
                  {e.memo || <span className="text-muted-foreground">—</span>}
                  {e.reference && <span className="text-xs text-muted-foreground ml-2">({e.reference})</span>}
                </span>
              ),
            },
            { key: "basis", label: "Basis", width: 100, sortable: true, groupable: true, filterType: "select",
              filterOptions: [{ value: "accrual", label: "accrual" }, { value: "cash", label: "cash" }],
              render: (e) => <Badge variant="outline">{e.basis}</Badge> },
            { key: "currency", label: "Currency", width: 100, sortable: true, groupable: true, filterType: "select",
              filterOptions: [{ value: "INR", label: "INR" }, { value: "USD", label: "USD" }, { value: "EUR", label: "EUR" }] },
            { key: "total_debit_native", label: "Debit total", width: 160, sortable: true, align: "right", type: "currency", sum: true,
              accessor: (e) => Number(e.total_debit_native || 0),
              render: (e) => <span className="font-mono">{fmtMoney(e.total_debit_native, e.currency)}</span> },
          ]}
          emptyTitle="No entries yet"
          emptyDescription="Post double-entry journals with balanced debits and credits."
        />
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="journal-create-dialog">
          <DialogHeader>
            <DialogTitle>New journal entry</DialogTitle>
            <DialogDescription>Debits must equal credits before posting (double-entry).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date</Label><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="journal-date-input" /></div>
              <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} data-testid="journal-currency-input" /></div>
              <div><Label>Basis</Label>
                <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                  <SelectTrigger data-testid="journal-basis-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="accrual">Accrual</SelectItem><SelectItem value="cash">Cash</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Memo</Label><Textarea rows={2} value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} data-testid="journal-memo-input" /></div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label>Lines</Label><Button variant="outline" size="sm" onClick={addLine} data-testid="journal-add-line">+ Line</Button></div>
              <table className="w-full text-sm border rounded">
                <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Account</th><th className="p-2 text-right">Debit</th><th className="p-2 text-right">Credit</th><th className="p-2 w-8"></th></tr></thead>
                <tbody>
                  {form.lines.map((l, i) => (
                    <tr key={i} className="border-t" data-testid={`journal-line-${i}`}>
                      <td className="p-2">
                        <Select value={l.account_id} onValueChange={(v) => updateLine(i, { account_id: v })}>
                          <SelectTrigger data-testid={`journal-line-${i}-account`}><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="p-2"><Input type="number" step="0.01" value={l.debit} onChange={(e) => updateLine(i, { debit: e.target.value, credit: 0 })} className="text-right font-mono" data-testid={`journal-line-${i}-debit`} /></td>
                      <td className="p-2"><Input type="number" step="0.01" value={l.credit} onChange={(e) => updateLine(i, { credit: e.target.value, debit: 0 })} className="text-right font-mono" data-testid={`journal-line-${i}-credit`} /></td>
                      <td className="p-2 text-right">{form.lines.length > 2 && <Button variant="ghost" size="sm" onClick={() => rmLine(i)}>×</Button>}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t bg-muted/20 text-sm font-mono">
                  <tr><td className="p-2 text-right font-medium">Totals</td><td className="p-2 text-right">{fmtMoney(totals.d, form.currency)}</td><td className="p-2 text-right">{fmtMoney(totals.c, form.currency)}</td><td /></tr>
                </tfoot>
              </table>
              <div className="mt-2 text-sm" data-testid="journal-balance-indicator">
                {totals.balanced ? <span className="text-emerald-600">✓ Balanced</span> : <span className="text-rose-600">Δ {fmtMoney(totals.d - totals.c, form.currency)} — must balance</span>}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={submit} disabled={!totals.balanced} data-testid="journal-submit">Post entry</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────── Expenses ─────────────────────

function ExpensesTab({ wsId }) {
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tds, setTds] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = {
    expense_date: new Date().toISOString().slice(0, 10),
    vendor_name: "", category_account_id: "", payment_account_id: "",
    amount: 0, currency: "INR",
    hsn_sac_code: "", place_of_supply: "", cgst_pct: 0, sgst_pct: 0, igst_pct: 0, cess_pct: 0,
    vendor_gstin: "", tds_section: "", tds_rate: 0, notes: "", basis: "accrual",
  };
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get(`/finance/workspaces/${wsId}/expenses?limit=200`).then((r) => setExpenses(r.data.expenses || []));
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccounts(r.data.accounts || []));
    api.get(`/finance/tds-sections`).then((r) => setTds(r.data.sections || []));
  }, [wsId]);

  const expenseAccts = accounts.filter((a) => a.type === "expense");
  const paymentAccts = accounts.filter((a) => ["bank", "cash", "payable"].includes(a.subtype));

  const preview = useMemo(() => {
    const net = Number(form.amount) || 0;
    const cgst = (net * Number(form.cgst_pct || 0)) / 100;
    const sgst = (net * Number(form.sgst_pct || 0)) / 100;
    const igst = (net * Number(form.igst_pct || 0)) / 100;
    const tds = (net * Number(form.tds_rate || 0)) / 100;
    const gross = net + cgst + sgst + igst;
    return { net, cgst, sgst, igst, tds, gross, netPayable: gross - tds };
  }, [form]);

  const submit = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount), cgst_pct: Number(form.cgst_pct), sgst_pct: Number(form.sgst_pct), igst_pct: Number(form.igst_pct), cess_pct: Number(form.cess_pct), tds_rate: Number(form.tds_rate) };
      await api.post(`/finance/workspaces/${wsId}/expenses`, payload);
      toast.success("Expense recorded — journal auto-posted");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{expenses.length} expenses</div>
        <Button onClick={() => setOpen(true)} data-testid="expenses-add-btn"><Plus className="w-4 h-4 mr-2" />New expense</Button>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40"><tr className="text-left"><th className="p-3">Date</th><th className="p-3">Vendor</th><th className="p-3">HSN/SAC</th><th className="p-3">GST</th><th className="p-3">TDS</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Net payable</th></tr></thead>
          <tbody>
            {expenses.map((x) => (
              <tr key={x.id} className="border-t" data-testid={`expense-row-${x.id}`}>
                <td className="p-3">{x.expense_date}</td>
                <td className="p-3">{x.vendor_name}</td>
                <td className="p-3 font-mono text-xs">{x.hsn_sac_code || "—"}</td>
                <td className="p-3 text-xs">{x.cgst_pct}% / {x.sgst_pct}% / {x.igst_pct}%</td>
                <td className="p-3 text-xs">{x.tds_section ? `${x.tds_section} @ ${x.tds_rate}%` : "—"}</td>
                <td className="p-3 text-right font-mono">{fmtMoney(x.gross_amount, x.currency)}</td>
                <td className="p-3 text-right font-mono">{fmtMoney(x.net_payable, x.currency)}</td>
              </tr>
            ))}
            {expenses.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No expenses yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="expenses-create-dialog">
          <DialogHeader>
            <DialogTitle>New expense</DialogTitle>
            <DialogDescription>GST + TDS values will auto-post to the ledger as a balanced journal entry.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date-input" /></div>
            <div><Label>Vendor</Label><Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="expense-vendor-input" /></div>
            <div><Label>Expense account</Label>
              <Select value={form.category_account_id} onValueChange={(v) => setForm({ ...form, category_account_id: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{expenseAccts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Payment / AP account</Label>
              <Select value={form.payment_account_id} onValueChange={(v) => setForm({ ...form, payment_account_id: v })}>
                <SelectTrigger data-testid="expense-payment-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{paymentAccts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount (net)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} data-testid="expense-currency-input" /></div>
            <div><Label>HSN / SAC code</Label><Input value={form.hsn_sac_code} onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value })} data-testid="expense-hsn-input" /></div>
            <div><Label>Place of supply</Label><Input value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value.toUpperCase().slice(0, 2) })} data-testid="expense-pos-input" placeholder="MH" /></div>
            <div><Label>CGST %</Label><Input type="number" step="0.01" value={form.cgst_pct} onChange={(e) => setForm({ ...form, cgst_pct: e.target.value })} data-testid="expense-cgst-input" /></div>
            <div><Label>SGST %</Label><Input type="number" step="0.01" value={form.sgst_pct} onChange={(e) => setForm({ ...form, sgst_pct: e.target.value })} data-testid="expense-sgst-input" /></div>
            <div><Label>IGST %</Label><Input type="number" step="0.01" value={form.igst_pct} onChange={(e) => setForm({ ...form, igst_pct: e.target.value })} data-testid="expense-igst-input" /></div>
            <div><Label>Vendor GSTIN</Label><Input value={form.vendor_gstin} onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value })} data-testid="expense-gstin-input" /></div>
            <div><Label>TDS section</Label>
              <Select value={form.tds_section} onValueChange={(v) => { const t = tds.find((s) => s.code === v); setForm({ ...form, tds_section: v, tds_rate: t?.default_rate || 0 }); }}>
                <SelectTrigger data-testid="expense-tds-section"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{tds.map((s) => <SelectItem key={s.code} value={s.code}>{s.code} — {s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>TDS rate %</Label><Input type="number" step="0.01" value={form.tds_rate} onChange={(e) => setForm({ ...form, tds_rate: e.target.value })} data-testid="expense-tds-rate" /></div>
          </div>
          <div className="mt-4 p-3 rounded border bg-muted/20 text-sm font-mono space-y-1" data-testid="expense-preview">
            <div className="flex justify-between"><span>Net:</span><span>{fmtMoney(preview.net, form.currency)}</span></div>
            <div className="flex justify-between"><span>+ GST (CGST+SGST+IGST):</span><span>{fmtMoney(preview.cgst + preview.sgst + preview.igst, form.currency)}</span></div>
            <div className="flex justify-between font-semibold"><span>Gross:</span><span>{fmtMoney(preview.gross, form.currency)}</span></div>
            <div className="flex justify-between"><span>− TDS:</span><span>{fmtMoney(preview.tds, form.currency)}</span></div>
            <div className="flex justify-between font-semibold border-t pt-1"><span>Net payable:</span><span>{fmtMoney(preview.netPayable, form.currency)}</span></div>
          </div>
          <DialogFooter><Button onClick={submit} disabled={!form.vendor_name || !form.category_account_id || !form.payment_account_id || !form.amount} data-testid="expense-submit">Record expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────── Budgets ─────────────────────

function BudgetsTab({ wsId }) {
  const [budgets, setBudgets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [reconOpen, setReconOpen] = useState(false);
  const [selBudget, setSelBudget] = useState(null);
  const [recon, setRecon] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [planningBudget, setPlanningBudget] = useState(null);
  const [form, setForm] = useState({ name: "", fiscal_year: "2026-27", period_type: "monthly", currency: "INR", lines: [{ account_id: "", period_label: "", manual_amount: 0 }] });

  const load = () => api.get(`/finance/workspaces/${wsId}/budgets`).then((r) => setBudgets(r.data.budgets || []));
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccounts(r.data.accounts || []));
  }, [wsId]);

  const updateLine = (i, patch) => { const lines = [...form.lines]; lines[i] = { ...lines[i], ...patch }; setForm({ ...form, lines }); };
  const submit = async () => {
    try {
      const payload = { ...form, lines: form.lines.filter((l) => l.account_id).map((l) => ({ ...l, manual_amount: Number(l.manual_amount) || 0 })) };
      await api.post(`/finance/workspaces/${wsId}/budgets`, payload);
      toast.success("Budget created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const reconcile = async (b) => {
    try {
      const { data } = await api.get(`/finance/workspaces/${wsId}/budgets/${b.id}/reconcile`);
      setSelBudget(b); setRecon(data); setNarrative(null); setReconOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const generateNarrative = async () => {
    if (!selBudget) return;
    setNarrativeBusy(true);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/budgets/${selBudget.id}/variance-narrative?material_threshold_pct=10`);
      setNarrative(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setNarrativeBusy(false); }
  };

  const acctMap = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{budgets.length} budgets</div>
        <Button onClick={() => setOpen(true)} data-testid="budgets-add-btn"><Plus className="w-4 h-4 mr-2" />New budget</Button>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40"><tr className="text-left"><th className="p-3">Name</th><th className="p-3">FY</th><th className="p-3">Period</th><th className="p-3">Currency</th><th className="p-3">Lines</th><th className="p-3"></th></tr></thead>
          <tbody>
            {budgets.map((b) => (
              <tr key={b.id} className="border-t" data-testid={`budget-row-${b.id}`}>
                <td className="p-3 font-medium">{b.name}</td>
                <td className="p-3">{b.fiscal_year}</td>
                <td className="p-3"><Badge variant="outline">{b.period_type}</Badge></td>
                <td className="p-3">{b.currency}</td>
                <td className="p-3">{(b.lines || []).length}</td>
                <td className="p-3 text-right space-x-1">
                  <Button variant="outline" size="sm" onClick={() => setPlanningBudget(b)} data-testid={`budget-planning-${b.id}`}><LineChart className="w-3.5 h-3.5 mr-1" />Planning</Button>
                  <Button variant="outline" size="sm" onClick={() => reconcile(b)} data-testid={`budget-reconcile-${b.id}`}>Reconcile</Button>
                </td>
              </tr>
            ))}
            {budgets.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No budgets yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="budgets-create-dialog">
          <DialogHeader>
            <DialogTitle>New budget</DialogTitle>
            <DialogDescription>Plan spend per account × period. Reconcile against system actuals from journal entries.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="budget-name-input" /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} data-testid="budget-currency-input" /></div>
            <div><Label>Fiscal year</Label><Input value={form.fiscal_year} onChange={(e) => setForm({ ...form, fiscal_year: e.target.value })} placeholder="2026-27" data-testid="budget-fy-input" /></div>
            <div className="col-span-2"><Label>Period type</Label>
              <Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}>
                <SelectTrigger data-testid="budget-period-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly (YYYY-MM)</SelectItem><SelectItem value="quarterly">Quarterly (YYYY-Qn)</SelectItem><SelectItem value="yearly">Yearly (YYYY)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2"><Label>Lines</Label>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, lines: [...form.lines, { account_id: "", period_label: "", manual_amount: 0 }] })} data-testid="budget-add-line">+ Line</Button>
            </div>
            <table className="w-full text-sm border rounded">
              <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Account</th><th className="p-2 text-left">Period</th><th className="p-2 text-right">Manual amount</th></tr></thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-2">
                      <Select value={l.account_id} onValueChange={(v) => updateLine(i, { account_id: v })}>
                        <SelectTrigger data-testid={`budget-line-${i}-account`}><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-2"><Input value={l.period_label} onChange={(e) => updateLine(i, { period_label: e.target.value })} placeholder="2026-07" data-testid={`budget-line-${i}-period`} /></td>
                    <td className="p-2"><Input type="number" step="0.01" value={l.manual_amount} onChange={(e) => updateLine(i, { manual_amount: e.target.value })} className="text-right font-mono" data-testid={`budget-line-${i}-amount`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button onClick={submit} disabled={!form.name} data-testid="budget-submit">Create budget</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reconOpen} onOpenChange={setReconOpen}>
        <DialogContent className="max-w-3xl" data-testid="budget-reconcile-dialog">
          <DialogHeader>
            <DialogTitle>Reconciliation — {selBudget?.name}</DialogTitle>
            <DialogDescription>Manual budget vs system actuals (from posted journal entries).</DialogDescription>
          </DialogHeader>
          {recon && (
            <>
              <table className="w-full text-sm border rounded">
                <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Account</th><th className="p-2 text-left">Period</th><th className="p-2 text-right">Manual</th><th className="p-2 text-right">Actual</th><th className="p-2 text-right">Variance</th><th className="p-2 text-right">Var %</th></tr></thead>
                <tbody>
                  {recon.lines.map((l, i) => (
                    <tr key={i} className="border-t" data-testid={`recon-row-${i}`}>
                      <td className="p-2">{acctMap[l.account_id]?.code} — {acctMap[l.account_id]?.name}</td>
                      <td className="p-2">{l.period_label}</td>
                      <td className="p-2 text-right font-mono">{fmtMoney(l.manual_amount)}</td>
                      <td className="p-2 text-right font-mono">{fmtMoney(l.actual_amount)}</td>
                      <td className={`p-2 text-right font-mono ${l.variance > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtMoney(l.variance)}</td>
                      <td className={`p-2 text-right font-mono ${(l.variance_pct || 0) > 0 ? "text-rose-600" : "text-emerald-600"}`}>{l.variance_pct == null ? "—" : `${l.variance_pct}%`}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t bg-muted/20 font-mono text-sm">
                  <tr><td className="p-2 font-medium" colSpan={2}>Totals</td><td className="p-2 text-right">{fmtMoney(recon.totals.manual)}</td><td className="p-2 text-right">{fmtMoney(recon.totals.actual)}</td><td className={`p-2 text-right ${recon.totals.variance > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtMoney(recon.totals.variance)}</td><td /></tr>
                </tfoot>
              </table>
              <div className="mt-4 pt-4 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-sm">AI variance narrative</h4>
                    <p className="text-xs text-muted-foreground">Get a CFO-grade explanation for each material variance (grounded in your ledger).</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={generateNarrative} disabled={narrativeBusy} data-testid="budget-narrative-btn">
                    {narrativeBusy ? "Analyzing…" : "✨ Generate narrative"}
                  </Button>
                </div>
                {narrative && (
                  <div className="space-y-3" data-testid="budget-narrative-result">
                    {narrative.summary && (
                      <div className="p-3 rounded border bg-primary/5 text-sm">
                        <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Executive summary</div>
                        <p className="whitespace-pre-wrap">{narrative.summary}</p>
                      </div>
                    )}
                    {narrative.narratives?.length > 0 && (
                      <div className="space-y-2">
                        {narrative.narratives.map((n, i) => (
                          <div key={i} className="p-3 rounded border text-sm" data-testid={`narrative-line-${i}`}>
                            <div className="text-xs text-muted-foreground mb-1"><span className="font-mono">{n.account_code}</span> · {n.period}</div>
                            <p className="whitespace-pre-wrap">{n.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {narrative.narratives?.length === 0 && !narrative.summary && (
                      <p className="text-sm text-muted-foreground">All lines within threshold — no narrative required.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {planningBudget && (
        <BudgetPlanningDialog
          wsId={wsId}
          budget={planningBudget}
          onClose={() => setPlanningBudget(null)}
        />
      )}
    </div>
  );
}

// ───────────────────── Settings ─────────────────────

function SettingsTab({ wsId }) {
  const [settings, setSettings] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [gstins, setGstins] = useState([]);
  const [fxRates, setFxRates] = useState([]);
  const [gstinForm, setGstinForm] = useState({ gstin: "", legal_name: "", state_code: "", is_primary: false });
  const [fxForm, setFxForm] = useState({ from_currency: "USD", to_currency: "INR", rate: 83, as_of: new Date().toISOString().slice(0, 10) });
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  const loadAll = () => {
    api.get(`/finance/workspaces/${wsId}/settings`).then((r) => setSettings(r.data));
    api.get(`/finance/currencies`).then((r) => setCurrencies(r.data.currencies || []));
    api.get(`/finance/workspaces/${wsId}/gstins`).then((r) => setGstins(r.data.gstins || []));
    api.get(`/finance/workspaces/${wsId}/fx-rates`).then((r) => setFxRates(r.data.rates || []));
  };
  useEffect(() => { if (wsId) loadAll(); }, [wsId]);

  const savePref = async (patch) => {
    try { const { data } = await api.patch(`/finance/workspaces/${wsId}/settings`, patch); setSettings(data); toast.success("Saved"); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const addGstin = async () => {
    try {
      await api.post(`/finance/workspaces/${wsId}/gstins`, gstinForm);
      toast.success("GSTIN added"); setGstinForm({ gstin: "", legal_name: "", state_code: "", is_primary: false }); loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const addFx = async () => {
    try {
      await api.post(`/finance/workspaces/${wsId}/fx-rates`, { ...fxForm, rate: Number(fxForm.rate) });
      toast.success("Rate saved"); loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const downloadGstr = (report) => {
    const url = `${api.defaults.baseURL}/finance/workspaces/${wsId}/gstr/${report}/export.csv?period=${period}`;
    const token = localStorage.getItem("co_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `gstr-${report}-${period}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      });
  };

  if (!settings) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">Workspace finance settings</h3>
          <div><Label>Base currency</Label>
            <Select value={settings.base_currency} onValueChange={(v) => savePref({ base_currency: v })}>
              <SelectTrigger data-testid="settings-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Accounting method</Label>
            <Select value={settings.accounting_method} onValueChange={(v) => savePref({ accounting_method: v })}>
              <SelectTrigger data-testid="settings-method-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="accrual">Accrual</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Fiscal year start month</Label>
            <Input
              type="number" min="1" max="12"
              value={settings.fiscal_year_start_month}
              onChange={(e) => setSettings({ ...settings, fiscal_year_start_month: e.target.value })}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (v >= 1 && v <= 12 && v !== settings.fiscal_year_start_month) savePref({ fiscal_year_start_month: v });
              }}
              data-testid="settings-fy-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">GSTIN registry</h3>
          <div className="space-y-2">
            {gstins.map((g) => (
              <div key={g.id} className="flex items-center justify-between border-b py-2 text-sm" data-testid={`gstin-row-${g.gstin}`}>
                <div><div className="font-mono">{g.gstin}</div><div className="text-xs text-muted-foreground">{g.legal_name} · {g.state_code}</div></div>
                {g.is_primary && <Badge variant="outline">Primary</Badge>}
              </div>
            ))}
            {gstins.length === 0 && <p className="text-sm text-muted-foreground">No GSTINs registered.</p>}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
            <div className="col-span-2"><Label>GSTIN (15 chars)</Label><Input maxLength={15} value={gstinForm.gstin} onChange={(e) => setGstinForm({ ...gstinForm, gstin: e.target.value.toUpperCase() })} data-testid="gstin-input" /></div>
            <div className="col-span-2"><Label>Legal name</Label><Input value={gstinForm.legal_name} onChange={(e) => setGstinForm({ ...gstinForm, legal_name: e.target.value })} data-testid="gstin-legalname-input" /></div>
            <div><Label>State code (2)</Label><Input maxLength={2} value={gstinForm.state_code} onChange={(e) => setGstinForm({ ...gstinForm, state_code: e.target.value.toUpperCase() })} data-testid="gstin-state-input" /></div>
            <div className="flex items-end"><Button onClick={addGstin} disabled={gstinForm.gstin.length !== 15 || !gstinForm.legal_name} data-testid="gstin-add-btn"><Plus className="w-4 h-4 mr-2" />Add</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">FX rates</h3>
          <div className="space-y-2 max-h-[240px] overflow-auto">
            {fxRates.map((r) => (
              <div key={`${r.from_currency}-${r.to_currency}-${r.as_of}`} className="flex items-center justify-between text-sm border-b py-1 font-mono">
                <span>{r.from_currency} → {r.to_currency}</span><span>{r.rate}</span><span className="text-xs text-muted-foreground">{r.as_of}</span>
              </div>
            ))}
            {fxRates.length === 0 && <p className="text-sm text-muted-foreground">No FX rates set.</p>}
          </div>
          <div className="grid grid-cols-4 gap-2 pt-2 border-t items-end">
            <div><Label>From</Label><Input value={fxForm.from_currency} onChange={(e) => setFxForm({ ...fxForm, from_currency: e.target.value.toUpperCase() })} data-testid="fx-from-input" /></div>
            <div><Label>To</Label><Input value={fxForm.to_currency} onChange={(e) => setFxForm({ ...fxForm, to_currency: e.target.value.toUpperCase() })} data-testid="fx-to-input" /></div>
            <div><Label>Rate</Label><Input type="number" step="0.0001" value={fxForm.rate} onChange={(e) => setFxForm({ ...fxForm, rate: e.target.value })} data-testid="fx-rate-input" /></div>
            <Button onClick={addFx} data-testid="fx-add-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">GSTR export</h3>
          <p className="text-xs text-muted-foreground">Download monthly GSTR-1 (outward) and GSTR-3B (summary) as CSV.</p>
          <div className="flex items-end gap-2">
            <div><Label>Period (YYYY-MM)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="gstr-period-input" /></div>
            <Button variant="outline" onClick={() => downloadGstr("1")} data-testid="gstr-1-btn"><Download className="w-4 h-4 mr-2" />GSTR-1</Button>
            <Button variant="outline" onClick={() => downloadGstr("3b")} data-testid="gstr-3b-btn"><Download className="w-4 h-4 mr-2" />GSTR-3B</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
